import {
  useState,
  useEffect,
  createContext,
  type FC,
  type ComponentType,
} from 'react';
import {
  type DataLayerArgs
} from 'react-gtm-module';
import {
  useSessionSelector,
  selectIsAuthenticated,
} from '@entities/session';
import {
  GTM_ID_1903,
  ENVIRONMENT
} from '../config';
import {
  ConfirmCookieConsentBanner
} from './confirm-cookie-consent-banner.component';

const isProd = ENVIRONMENT === 'prod';

export type GoogleTagManagerContext1903Type = {
  sendDataToGtm: (data: DataLayerArgs) => void;
};

export const GoogleTagManager1903Context = createContext<Maybe<GoogleTagManagerContext1903Type>>(null);

// TODO: move to shared
export const withGoogleTagManager1903 = <P extends object>(
  WrappedComponent: ComponentType<P>
): FC<P> => (props: P) => {
  const isAuthenticated = useSessionSelector(selectIsAuthenticated);
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [consentGiven, setConsentGiven] = useState<boolean>(false);

  useEffect(() => {
    if (isAuthenticated) {
      setConsentGiven(true);
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    initializeGoogleTagManager();
  }, [consentGiven, isAuthenticated]);

  const initializeGoogleTagManager = async () => {
    if (isProd && !initialized && (isAuthenticated || consentGiven)) {
      const TagManager = await import('react-gtm-module');
      TagManager.default.initialize({ gtmId: GTM_ID_1903 });
      setInitialized(true);
    }
  };

  const handleCookieAccepted = () => {
    setConsentGiven(true);
    setShowBanner(false);
  };

  const sendDataToGtm = async (data: DataLayerArgs) => {
    if (initialized && (isAuthenticated || consentGiven) && isProd) {
      const TagManager = await import('react-gtm-module');
      TagManager.default.dataLayer(data);
    }
  };

  return (
    <GoogleTagManager1903Context.Provider value={{ sendDataToGtm }}>
      <WrappedComponent {...props} />
      {showBanner && (
        <ConfirmCookieConsentBanner onAccepted={handleCookieAccepted} />
      )}
    </GoogleTagManager1903Context.Provider>
  );
};